import type {
  IPriceRangeCalculationStrategy,
  PriceRangeCalculationInput,
  PriceRangeCalculationResult,
} from "./interfaces"
import { MAX_TICK, MIN_TICK } from "../../constants"
import { roundToTickSpacing } from "../../utils/price-to-tick-conversion"

/**
 * Full Range Calculation Strategy
 * Handles calculations for full range positions (MIN_TICK to MAX_TICK)
 */
export class FullRangeCalculationStrategy implements IPriceRangeCalculationStrategy {
  calculate(input: PriceRangeCalculationInput): PriceRangeCalculationResult {
    const { tickSpacing, token0, token1, currentPrice } = input

    // Validate required inputs
    if (!tickSpacing || !token0 || !token1) {
      return {
        ticks: [undefined, undefined],
        pricesAtTicks: ["", ""],
        priceDifferences: { min: undefined, max: undefined },
        ticksAtLimit: [false, false],
        minDisplayValue: "",
        maxDisplayValue: "",
        hasValidRange: false,
      }
    }

    // Full range: use MIN_TICK and MAX_TICK
    const minTick = roundToTickSpacing(MIN_TICK, tickSpacing)
    const maxTick = roundToTickSpacing(MAX_TICK, tickSpacing)

    // Display: mimic Uniswap full range UI (0 — ∞)
    const minPriceString = "0"
    const maxPriceString = "∞"

    // Full range doesn't show meaningful +/- % deltas
    const priceDifferences = { min: undefined, max: undefined }

    // Display values - full range always shows prices (not percentages)
    const minDisplayValue = minPriceString
    const maxDisplayValue = maxPriceString

    // Ticks are at limits for full range
    const ticksAtLimit: [boolean, boolean] = [true, true]

    // Full range is always valid if we have ticks
    const hasValidRange = true

    return {
      ticks: [minTick, maxTick],
      pricesAtTicks: [minPriceString, maxPriceString],
      priceDifferences,
      ticksAtLimit,
      minDisplayValue,
      maxDisplayValue,
      hasValidRange,
    }
  }
}
